import { useEffect, useRef } from 'react';
import { prochitatSoglasie } from '@/lib/ga4-soglasie';

/**
 * События Google Analytics 4 (aisearch.tech, ingdg.com, datahunter.store).
 *
 * Имена одинаковые на всех трёх сайтах, чтобы отчёты сравнивались:
 *
 * | событие         | когда                                     | параметры                           |
 * |-----------------|-------------------------------------------|-------------------------------------|
 * | `tool_start`    | нажата главная кнопка инструмента         | `tool`                              |
 * | `tool_result`   | результат готов и показан                 | `tool`                              |
 * | `tool_download` | скачал или скопировал результат           | `tool`, `format`                    |
 * | `sign_up`       | завершена регистрация                     | `method`                            |
 * | `purchase`      | деньги зачислены                          | `value`, `currency`, `transaction_id` |
 * | `limit_reached` | упёрся в бесплатный предел                | `tool`                              |
 *
 * Обработчик общий для всех доменов, и на aisearch.ru он тоже лежит в
 * скриптах страницы. Там он молчит: счётчик ставит загрузчик
 * (`components/seo/ga4-klient.tsx`), а загрузчика на российском домене нет,
 * и `window.__ga4` не появляется. Так же молчит на стенде без флага отладки,
 * на localhost и на любом сайте, пока посетитель не нажал «Принять» (до
 * этого gtag.js на странице нет вовсе) или после того, как он отказался.
 *
 * **В GA не уходит ничего, кроме параметров из таблицы.** Ни текста
 * запроса, ни имён файлов, ни почты, имени, телефона, ни наших
 * идентификаторов пользователя. Лишний параметр выбрасывается, а значение
 * не того вида (пробелы, длинная строка) не отправляется: поле `tool` не
 * должно однажды принести в отчёт запрос человека.
 */

declare global {
  interface Window {
    /** Код потока: ставит загрузчик, когда счётчик включён на этом адресе. */
    __ga4?: string;
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

export type Ga4Sobytie =
  | 'tool_start'
  | 'tool_result'
  | 'tool_download'
  | 'sign_up'
  | 'purchase'
  | 'limit_reached';

export interface Ga4Parametry {
  tool?: string;
  format?: string;
  method?: string;
  value?: number;
  currency?: string;
  transaction_id?: string;
}

/** Какие параметры у какого события. Всё прочее не отправляется. */
const RAZRESHENO: Readonly<Record<Ga4Sobytie, readonly (keyof Ga4Parametry)[]>> = {
  tool_start: ['tool'],
  tool_result: ['tool'],
  tool_download: ['tool', 'format'],
  sign_up: ['method'],
  purchase: ['value', 'currency', 'transaction_id'],
  limit_reached: ['tool'],
};

/** События инструмента: без явного `tool` он берётся из адреса страницы. */
const S_INSTRUMENTOM: ReadonlySet<Ga4Sobytie> = new Set([
  'tool_start',
  'tool_result',
  'tool_download',
  'limit_reached',
]);

/** Короткое служебное имя: латиница, цифры, дефис, подчёркивание. */
const SLUZHEBNOE = /^[a-z0-9][a-z0-9_-]{0,39}$/;

function sluzhebnoe(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v.trim().toLowerCase();
  return SLUZHEBNOE.test(s) ? s : undefined;
}

/**
 * Параметры, которые разрешено отправить с событием.
 *
 * Отдельной функцией ради теста: это единственное место, где решается,
 * что уходит в чужую аналитику.
 */
export function ochistitParametry(
  imya: Ga4Sobytie,
  params: Record<string, unknown> = {},
): Record<string, string | number> {
  const itog: Record<string, string | number> = {};
  for (const klyuch of RAZRESHENO[imya]) {
    const v = params[klyuch];
    if (klyuch === 'value') {
      if (typeof v === 'number' && Number.isFinite(v) && v >= 0) {
        itog.value = Math.round(v * 100) / 100;
      }
    } else if (klyuch === 'currency') {
      if (typeof v === 'string' && /^[A-Za-z]{3}$/.test(v)) itog.currency = v.toUpperCase();
    } else if (klyuch === 'transaction_id') {
      // Только непрозрачный номер платежа: без пробелов, @ и прочего,
      // по чему в нём можно было бы узнать человека.
      if (typeof v === 'string' && /^[A-Za-z0-9_-]{1,64}$/.test(v)) itog.transaction_id = v;
    } else {
      const s = sluzhebnoe(v);
      if (s !== undefined) itog[klyuch] = s;
    }
  }
  return itog;
}

/** Языковой префикс адреса: на .tech и ingdg.com он бывает, а бывает и нет. */
const PREFIKS_YAZYKA = /^\/(en|es|sr|ru)(?=\/|$)/;

/** Часть адреса, которая что-то значит сама по себе, а не номер записи. */
function nomerZapisi(s: string): boolean {
  return /^[0-9a-f-]{16,}$/i.test(s) || /^\d+$/.test(s);
}

/**
 * Имя инструмента по адресу страницы: последняя значащая часть пути.
 *
 * `/audio-to-text` → `audio-to-text`, `/es/remove-background` →
 * `remove-background`, `/dashboard/chat/5f1c…` → `chat`, главная → `home`.
 * Адрес страницы, а не отдельный справочник: так `tool` совпадает с
 * разделом отчёта «Страницы», и новый инструмент считается сам, без правки
 * списка.
 */
export function instrumentIzPuti(pathname: string): string {
  const chasti = pathname
    .replace(PREFIKS_YAZYKA, '')
    .split('/')
    .filter((s) => s !== '' && !nomerZapisi(s));
  const posled = chasti[chasti.length - 1];
  if (posled === undefined) return 'home';
  let imya = posled;
  try {
    imya = decodeURIComponent(posled);
  } catch {
    // Битая кодировка: остаётся как есть и отсеется проверкой ниже.
  }
  return sluzhebnoe(imya) ?? 'other';
}

/**
 * Метки рекламных кампаний, которые остаются в адресе страницы.
 *
 * Всё прочее из строки запроса в GA не уходит: в ней бывает текст
 * человека (`/app/search?q=…` на DataHunter, `/dashboard/chat/…?q=` в
 * кабинете) и личные ключи из писем (`/otpiska?t=…`).
 */
const METKI = /^(utm_(source|medium|campaign|term|content|id)|gclid|gbraid|wbraid)$/;

/**
 * Адрес страницы для GA: без строки запроса (кроме меток кампаний), без
 * якоря, с номерами записей кабинета вместо идентификаторов.
 */
export function adresDlyaGa(href: string): string {
  let u: URL;
  try {
    u = new URL(href);
  } catch {
    return '';
  }
  const metki = new URLSearchParams();
  u.searchParams.forEach((v, k) => {
    if (METKI.test(k)) metki.append(k, v);
  });
  const put = u.pathname
    .split('/')
    .map((s) => (nomerZapisi(s) ? ':id' : s))
    .join('/');
  const zapros = metki.toString();
  return `${u.origin}${put}${zapros ? `?${zapros}` : ''}`;
}

/**
 * Заголовок страницы для GA.
 *
 * Обычно это заголовок вкладки. Но у выдачи поиска DataHunter в нём стоит
 * сам запрос («sales — поиск наборов данных»), а в кабинете — названия
 * разговоров и файлов. Там вместо заголовка уходит путь страницы без
 * номеров: по нему отчёт всё так же складывается по разделам.
 */
export function zagolovokDlyaGa(href: string, title: string): string {
  let u: URL;
  try {
    u = new URL(href);
  } catch {
    return '';
  }
  // Флаг отладки — наш собственный, текста человека в нём нет.
  const estZapros = [...u.searchParams.keys()].some((k) => !METKI.test(k) && k !== 'ga_debug');
  const kabinet = /^\/dashboard(\/|$)/.test(u.pathname.replace(PREFIKS_YAZYKA, ''));
  if (!estZapros && !kabinet) return title;
  return new URL(adresDlyaGa(href)).pathname;
}

/**
 * Счётчик GA4 запущен на этой странице и посетитель согласен.
 *
 * Согласие проверяется и здесь: после отзыва уже загруженный gtag.js
 * остаётся на странице до перезагрузки, и без этой проверки события ушли
 * бы обезличенными сигналами без cookie, а мы обещаем, что после «Отказаться»
 * в Google не уходит ничего.
 */
export function ga4Vklyuchen(): boolean {
  return (
    typeof window !== 'undefined' &&
    Boolean(window.__ga4) &&
    Boolean(window.gtag) &&
    prochitatSoglasie() === 'granted'
  );
}

function sobrat(imya: Ga4Sobytie, params?: Ga4Parametry): Record<string, string | number> {
  const p = ochistitParametry(imya, params as Record<string, unknown> | undefined);
  if (S_INSTRUMENTOM.has(imya) && p.tool === undefined) {
    p.tool = instrumentIzPuti(window.location.pathname);
  }
  return p;
}

/**
 * События, случившиеся до запуска счётчика.
 *
 * Загрузчик — отдельный кусок скриптов (`ga4-lenivyj.tsx`) и стартует
 * позже, чем страница рисует выдачу: на DataHunter `tool_start` поиска
 * уходил раньше, чем появлялся счётчик, и терялся (замерено 16.10). Такие
 * события ждут здесь и уходят сразу после первого просмотра страницы.
 * Так же ждут события до «Принять»: счётчик грузится только по нему.
 *
 * Где счётчика нет совсем (aisearch.ru, стенд) или согласия так и не
 * было, очередь остаётся в памяти вкладки и никуда не отправляется, а
 * «Отказаться» её стирает. Хранятся только последние `PREDEL_OCHEREDI`
 * событий: за долгий сеанс без выбора очередь не растёт.
 */
const OCHERED: [Ga4Sobytie, Record<string, string | number>][] = [];
const PREDEL_OCHEREDI = 50;

/** Событие GA4. Без счётчика на этом адресе ничего не отправляет. */
export function ga4(imya: Ga4Sobytie, params?: Ga4Parametry): void {
  if (typeof window === 'undefined') return;
  try {
    const p = sobrat(imya, params);
    if (!ga4Vklyuchen()) {
      OCHERED.push([imya, p]);
      if (OCHERED.length > PREDEL_OCHEREDI) OCHERED.splice(0, OCHERED.length - PREDEL_OCHEREDI);
      return;
    }
    window.gtag?.('event', imya, p);
  } catch {
    // Аналитика не вправе ломать то, ради чего человек пришёл.
  }
}

/** Отправить накопленное до запуска. Зовёт загрузчик после первого просмотра. */
export function vylitOchered(): void {
  if (!ga4Vklyuchen()) return;
  for (const [imya, p] of OCHERED.splice(0)) window.gtag?.('event', imya, p);
}

/** Забыть накопленное: посетитель отказался, отправлять это некуда. */
export function ochistitOchered(): void {
  OCHERED.length = 0;
}

/**
 * Событие перед уходом со страницы: ждёт отправки, но не дольше `ms`.
 *
 * Регистрация заканчивается полной перезагрузкой, и обычный вызов может не
 * успеть уйти. Предел времени обязателен: заблокированный счётчик не
 * должен держать человека на форме.
 */
export function ga4IZhdat(imya: Ga4Sobytie, params?: Ga4Parametry, ms = 300): Promise<void> {
  if (!ga4Vklyuchen()) return Promise.resolve();
  return new Promise((resolve) => {
    const t = window.setTimeout(resolve, ms);
    try {
      window.gtag?.('event', imya, {
        ...sobrat(imya, params),
        event_callback: () => {
          window.clearTimeout(t);
          resolve();
        },
        event_timeout: ms,
      });
    } catch {
      window.clearTimeout(t);
      resolve();
    }
  });
}

/**
 * Начало и результат инструмента по его фазе: одна строка в компоненте
 * вместо двух вызовов в разных ветках.
 *
 * Фаза `rabota` — человек запустил инструмент, `gotovo` — результат на
 * экране. Повторный запуск снова проходит через `rabota` и считается
 * заново.
 */
export function useGa4Faza(faza: string, tool?: string): void {
  // Последняя отправленная фаза. Строгий режим React в разработке
  // запускает эффект дважды, и без этой отметки в DebugView каждое
  // событие приходило парой.
  const byla = useRef<string | null>(null);
  useEffect(() => {
    if (byla.current === faza) return;
    byla.current = faza;
    if (faza === 'rabota') ga4('tool_start', { tool });
    else if (faza === 'gotovo') ga4('tool_result', { tool });
  }, [faza, tool]);
}
